import type { StripeCheckoutProps } from '@wegic/price-ui';
import { handleSubscriptionAndPointsAPI } from '@/components/PayModal/handle';
import globalEventEmitter from '@/modules/EventEmitter';
import { EVENTS } from '@/modules/EventEmitter/const';

const DEFAULT_PAYMENT_SUCCESS_TIMEOUT_MS = 10000; // 等待权益变更的超时时间

export interface PaymentSuccessWaiter {
  promise: Promise<boolean>;
  cancel: () => void;
}

export function createPaymentSuccessWaiter(
  timeoutMs = DEFAULT_PAYMENT_SUCCESS_TIMEOUT_MS,
): PaymentSuccessWaiter {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let settled = false;
  let resolvePromise: (received: boolean) => void = () => {};

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    globalEventEmitter.off(EVENTS.PAYMENT_SUCCESS, handleSuccess);
  };

  const settle = (received: boolean) => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    resolvePromise(received);
  };

  function handleSuccess() {
    settle(true);
  }

  const promise = new Promise<boolean>((resolve) => {
    resolvePromise = resolve;
    globalEventEmitter.on(EVENTS.PAYMENT_SUCCESS, handleSuccess);
    timeoutId = setTimeout(() => settle(false), timeoutMs);
  });

  return {
    promise,
    cancel: () => settle(false),
  };
}

export async function waitForPaymentSuccessOrRefresh(
  waiter = createPaymentSuccessWaiter(),
): Promise<boolean> {
  const receivedPaymentSuccess = await waiter.promise;

  if (!receivedPaymentSuccess) {
    await handleSubscriptionAndPointsAPI();
  }

  return receivedPaymentSuccess;
}

export function createEmbeddedCheckoutOptions(
  clientSecret: string,
  onComplete: () => void | Promise<void>,
): StripeCheckoutProps['options'] {
  return {
    fetchClientSecret: async () => clientSecret,
    onComplete,
  };
}
